import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import useCorrectionHistory from './useCorrectionHistory'

type Order = 'asc' | 'desc'
type OrderBy =
  | 'date'
  | 'location'
  | 'correctedNetSales'
  | 'correctedCheckCount'
  | 'overwrittenByNboDate'

interface CorrectionHistoryTableProps {
  refreshTrigger?: number
}

const CorrectionHistoryTable: React.FC<CorrectionHistoryTableProps> = ({
  refreshTrigger,
}) => {
  // Fetch correction history
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch,
  } = useCorrectionHistory()

  // Table state
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<OrderBy>('date')

  // Theme and responsive
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Refetch data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetch()
    }
  }, [refreshTrigger]) // Remove refetch from dependencies

  // Format date for display - handle timezone correctly
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    // Parse as UTC and format in local timezone to avoid date shifting
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Format number with commas for thousands
  const formatNumber = (
    value: number | undefined,
    isCurrency: boolean = false,
  ) => {
    if (value === undefined || value === null) return '-'

    const formattedNumber = value.toLocaleString('en-US', {
      minimumFractionDigits: isCurrency ? 2 : 0,
      maximumFractionDigits: isCurrency ? 2 : 0,
    })

    return isCurrency ? `$${formattedNumber}` : formattedNumber
  }

  // Handle sorting
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // Sort data
  const sortedData = useMemo(() => {
    if (!historyData || historyData.length === 0) return []

    return [...historyData].sort((a, b) => {
      let comparison = 0

      if (orderBy === 'date') {
        comparison = a.date.localeCompare(b.date)
      } else if (orderBy === 'location') {
        comparison = a.locationName.localeCompare(b.locationName)
      } else if (orderBy === 'correctedNetSales') {
        comparison = (a.correctedNetSales ?? 0) - (b.correctedNetSales ?? 0)
      } else if (orderBy === 'correctedCheckCount') {
        comparison = (a.correctedCheckCount ?? 0) - (b.correctedCheckCount ?? 0)
      } else if (orderBy === 'overwrittenByNboDate') {
        // Sort by presence of date first, then by date value
        const aHasDate = a.overwrittenByNboDate ? 1 : 0
        const bHasDate = b.overwrittenByNboDate ? 1 : 0
        if (aHasDate !== bHasDate) {
          comparison = aHasDate - bHasDate
        } else if (a.overwrittenByNboDate && b.overwrittenByNboDate) {
          comparison = a.overwrittenByNboDate.localeCompare(
            b.overwrittenByNboDate,
          )
        }
      }

      return order === 'asc' ? comparison : -comparison
    })
  }, [historyData, order, orderBy])

  if (isLoadingHistory) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (historyError) {
    return (
      <Typography variant="body1" color="error">
        {historyError}
      </Typography>
    )
  }

  if (!historyData || historyData.length === 0) {
    return (
      <Typography
        variant="body1"
        sx={{
          fontFamily: 'Lexend-Regular',
          fontSize: '0.875rem',
        }}
      >
        No any manual correction found in the latest 30 days
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: isMobile ? 'auto' : 'visible',
      }}
    >
      <TableContainer
        sx={{
          width: '100%',
          overflowX: isMobile ? 'auto' : 'visible',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
          },
        }}
      >
        <Table
          size="small"
          sx={{
            fontSize: isMobile ? '0.75rem' : '0.8rem',
            tableLayout: isMobile ? 'fixed' : 'auto',
            minWidth: isMobile ? '800px' : 'auto',
            '& .MuiTableCell-root': {
              padding: isMobile ? '6px 8px' : '8px 12px',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: isMobile ? '20%' : 'auto',
                  whiteSpace: 'nowrap',
                  minWidth: '120px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  width: isMobile ? '25%' : 'auto',
                  maxWidth: '300px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'location'}
                  direction={orderBy === 'location' ? order : 'asc'}
                  onClick={() => handleRequestSort('location')}
                >
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  width: isMobile ? '20%' : 'auto',
                  whiteSpace: 'nowrap',
                  minWidth: '140px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'correctedNetSales'}
                  direction={orderBy === 'correctedNetSales' ? order : 'asc'}
                  onClick={() => handleRequestSort('correctedNetSales')}
                >
                  Corrected Net Sales
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  width: isMobile ? '20%' : 'auto',
                  whiteSpace: 'nowrap',
                  minWidth: '160px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'correctedCheckCount'}
                  direction={orderBy === 'correctedCheckCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('correctedCheckCount')}
                >
                  Corrected Check Count
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  width: isMobile ? '15%' : 'auto',
                  whiteSpace: 'nowrap',
                  minWidth: '140px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'overwrittenByNboDate'}
                  direction={orderBy === 'overwrittenByNboDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('overwrittenByNboDate')}
                >
                  Overwritten By NBO
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow key={`${row.date}-${row.locationId}-${index}`}>
                <TableCell>{formatDate(row.date)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: '300px',
                    wordBreak: 'break-word',
                    whiteSpace: isMobile ? 'nowrap' : 'normal',
                    overflow: isMobile ? 'hidden' : 'visible',
                    textOverflow: isMobile ? 'ellipsis' : 'unset',
                  }}
                >
                  {row.locationName}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(row.correctedNetSales, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(row.correctedCheckCount, false)}
                </TableCell>
                <TableCell align="center">
                  {row.overwrittenByNboDate ? (
                    <Typography
                      sx={{
                        color: 'success.main',
                        fontFamily: 'Lexend-Regular',
                        fontSize: '0.875rem',
                      }}
                    >
                      {formatDate(row.overwrittenByNboDate)}
                    </Typography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default CorrectionHistoryTable
